import React from 'react';
import { Box, Chip, Divider, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SPECIAL_REQUEST_CATEGORIES } from '../../constants/appConstants';
import { formatDateTime } from '../../utils/dateFormat';
import {
  buildOrderDescriptionFromRequest,
  formatSpecialRequestOrderRef,
  getSpecialRequestOrderId,
  resolveRequestShapeLabel,
} from '../../utils/specialRequestDisplay';

function DetailRow({ label, children }) {
  if (children == null || children === '' || children === '—') return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

export default function SpecialRequestDetailView({ request, getStatusColor }) {
  const { t } = useTranslation();
  if (!request) return null;

  const d = request.details || {};
  const category = request.category;
  const summary = buildOrderDescriptionFromRequest(request);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Chip
          size="small"
          label={t(`specialRequests.statuses.${request.status}`)}
          color={getStatusColor?.(request.status) || 'default'}
        />
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(request.createdAt)}
        </Typography>
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary">{t('specialRequests.category')}</Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          {t(`specialRequests.categories.${category}`)}
        </Typography>
      </Box>

      <Divider />

      {category === SPECIAL_REQUEST_CATEGORIES.CERTIFIED && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <DetailRow label={t('inventory.col.shape')}>{resolveRequestShapeLabel(d)}</DetailRow>
          <DetailRow label={t('inventory.col.carat')}>{d.carat != null ? `${d.carat} ct` : null}</DetailRow>
          <DetailRow label={t('inventory.col.color')}>{d.color}</DetailRow>
          <DetailRow label={t('inventory.col.clarity')}>{d.clarity}</DetailRow>
        </Box>
      )}

      {category === SPECIAL_REQUEST_CATEGORIES.NON_CERTIFIED && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <DetailRow label={t('inventory.col.shape')}>{resolveRequestShapeLabel(d)}</DetailRow>
          <DetailRow label={t('specialRequests.lengthMm')}>{d.length != null ? `${d.length} mm` : null}</DetailRow>
          <DetailRow label={t('specialRequests.widthMm')}>{d.width != null ? `${d.width} mm` : null}</DetailRow>
        </Box>
      )}

      {(d.comment || category === SPECIAL_REQUEST_CATEGORIES.OTHER) && (
        <DetailRow label={category === SPECIAL_REQUEST_CATEGORIES.OTHER ? t('specialRequests.description') : t('specialRequests.comment')}>
          {category === SPECIAL_REQUEST_CATEGORIES.OTHER ? (request.description || d.comment) : d.comment}
        </DetailRow>
      )}

      {summary && category !== SPECIAL_REQUEST_CATEGORIES.OTHER && (
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{t('specialRequests.summary')}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{summary}</Typography>
        </Box>
      )}

      {request.statusNote && (
        <Box sx={{ bgcolor: 'action.selected', borderRadius: 1, p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{t('specialRequests.statusNote')}</Typography>
          <Typography variant="body2">{request.statusNote}</Typography>
        </Box>
      )}

      {getSpecialRequestOrderId(request) && (
        <DetailRow label={t('specialRequests.linkedOrder')}>
          <MuiLink
            component={Link}
            to={`/orders/${getSpecialRequestOrderId(request)}`}
            underline="hover"
            fontWeight={600}
          >
            #{formatSpecialRequestOrderRef(request.convertedOrder)}
          </MuiLink>
        </DetailRow>
      )}
    </Box>
  );
}
